import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import recallService from '../../../services/recall.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import RecallCreatePanel from './subparts/RecallCreatePanel';
import RecallDetailsModal from './subparts/RecallDetailsModal';
import RecallFilters from './subparts/RecallFilters';
import RecallRequestList from './subparts/RecallRequestList';
import RecallsSkeleton from './subparts/RecallsSkeleton';
import {
    DEFAULT_RECALL_FILTERS,
    DEFAULT_RECALL_SORT,
    RECALL_SORT_FIELDS,
    formatRecallCount,
} from './subparts/recallsQuery';
import './Recalls.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function Recalls() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const [filters, setFilters] = useState(DEFAULT_RECALL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_RECALL_FILTERS);
    const [sort, setSort] = useState(DEFAULT_RECALL_SORT);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchRecalls = useCallback(
        async (page, size, nextFilters, nextSort, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const response = await recallService.getRecallRequests(clinicId, page, size, {
                    sortBy: nextSort.sortBy,
                    sortOrder: nextSort.sortOrder,
                    searchField: nextFilters.searchField,
                    searchTerm: nextFilters.searchTerm,
                    statusFilter: nextFilters.status,
                    dateFrom: nextFilters.dateFrom,
                    dateTo: nextFilters.dateTo,
                });

                const data = response.data;
                setRecords(data.content || []);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
            } catch (error) {
                console.error('Failed to load recall requests', error);
                toast.error('Failed to load recall requests');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId]
    );

    useEffect(() => {
        fetchRecalls(pageNumber, pageSize, appliedFilters, sort);
    }, [fetchRecalls, pageNumber, pageSize, appliedFilters, sort]);

    const description = useMemo(
        () =>
            totalElements
                ? `Upload recall files and track processing · ${formatRecallCount(totalElements)} total request${totalElements === 1 ? '' : 's'}`
                : 'Upload recall files and track recall request processing for your clinic',
        [totalElements]
    );

    const handleApplyFilters = (nextFilters = filters) => {
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setPageNumber(0);
    };

    const handleClearFilters = () => {
        setFilters(DEFAULT_RECALL_FILTERS);
        setAppliedFilters(DEFAULT_RECALL_FILTERS);
        setSort(DEFAULT_RECALL_SORT);
        setPageNumber(0);
    };

    const handleRemoveChip = (field, clearValue = '') => {
        const next = { ...appliedFilters, [field]: clearValue };
        setFilters(next);
        setAppliedFilters(next);
        setPageNumber(0);
    };

    const handleSortChange = (field, value) => {
        setSort((prev) => ({ ...prev, [field]: value }));
        setPageNumber(0);
    };

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing) {
            return;
        }

        setIsRefreshing(true);

        try {
            await fetchRecalls(pageNumber, pageSize, appliedFilters, sort, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleCreated = () => {
        setPageNumber(0);
        fetchRecalls(0, pageSize, appliedFilters, sort, { silent: true });
    };

    const handleUploadComplete = () => {
        fetchRecalls(pageNumber, pageSize, appliedFilters, sort, { silent: true });
    };

    return (
        <div className="ecx-page recalls-page ecx-page--full-width">
            <SubHeader
                title="Recalls"
                description={description}
                icon="bi-person-check"
                containerClass="w-100 px-4"
            >
                <div className="d-flex align-items-center gap-2">
                    <Link to="/help?category=recalls" className="btn btn-sm btn-outline-primary rounded-pill">
                        <i className="bi bi-question-circle me-1"></i>
                        Help
                    </Link>
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        disabled={isRefreshing || loading}
                        title="Refresh recalls"
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                        Refresh
                    </button>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <RecallCreatePanel
                    clinicId={clinicId}
                    onCreated={handleCreated}
                    disabled={loading || isRefreshing}
                />

                <div className="ecx-page-card ecx-page-panel recalls-panel">
                    <RecallFilters
                        filters={filters}
                        appliedFilters={appliedFilters}
                        onFiltersChange={setFilters}
                        onApply={handleApplyFilters}
                        onClear={handleClearFilters}
                        onRemoveChip={handleRemoveChip}
                        loading={loading}
                    />

                    <div className="ecx-page-panel__header border-top-0">
                        <div className="recalls-toolbar">
                            <div className="recalls-toolbar__meta">
                                <h5 className="mb-0 fw-semibold text-dark">Recall requests</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    Page {totalPages === 0 ? 0 : pageNumber + 1} of {Math.max(totalPages, 1)}
                                </span>
                            </div>

                            <div className="recalls-toolbar__controls">
                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Sort
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '9.5rem' }}
                                        value={sort.sortBy}
                                        onChange={(event) => handleSortChange('sortBy', event.target.value)}
                                        disabled={loading}
                                    >
                                        {RECALL_SORT_FIELDS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '6.5rem' }}
                                        value={sort.sortOrder}
                                        onChange={(event) => handleSortChange('sortOrder', event.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="DESC">Desc</option>
                                        <option value="ASC">Asc</option>
                                    </select>
                                </label>

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setPageNumber(0);
                                        }}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column">
                        {loading ? (
                            <RecallsSkeleton count={pageSize > 5 ? 5 : pageSize} />
                        ) : (
                            <>
                                <RecallRequestList
                                    records={records}
                                    clinicId={clinicId}
                                    onViewDetails={setSelectedRequest}
                                    onUploadComplete={handleUploadComplete}
                                    loading={loading}
                                />

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top pt-3 mt-3">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <RecallDetailsModal
                show={Boolean(selectedRequest)}
                onClose={() => setSelectedRequest(null)}
                clinicId={clinicId}
                requestId={selectedRequest?.id}
                previewItem={selectedRequest}
                onUpdated={handleUploadComplete}
            />
        </div>
    );
}

export default Recalls;
